export type RuleListPagination = {
  page: number;
  limit: number;
  sort_by: string;
  sort_order: "asc" | "desc";
  search: string;
};

export type RuleListState = {
  paginationData: RuleListPagination;
  selectedPlatform: string;
  searchString: string;
};

export const DEFAULT_RULE_LIST_PAGINATION: RuleListPagination = {
  page: 1,
  limit: 10,
  sort_by: "",
  sort_order: "desc",
  search: "",
};

export const RULE_ENGINE_ALL_CHANNELS = "All";

export const parseRuleListStateFromSearchParams = (
  params: URLSearchParams
): RuleListState | undefined => {
  const hasAny =
    params.has("page") ||
    params.has("limit") ||
    params.has("search") ||
    params.has("sort_by") ||
    params.has("sort_order") ||
    params.has("platform");

  if (!hasAny) return undefined;

  const search = params.get("search") ?? "";

  return {
    paginationData: {
      page: Math.max(1, Number(params.get("page")) || 1),
      limit: Math.max(1, Number(params.get("limit")) || DEFAULT_RULE_LIST_PAGINATION.limit),
      sort_by: params.get("sort_by") ?? "",
      sort_order: params.get("sort_order") === "asc" ? "asc" : "desc",
      search,
    },
    selectedPlatform: params.get("platform") ?? RULE_ENGINE_ALL_CHANNELS,
    searchString: search,
  };
};

export const ruleListStateToSearchParams = (listState: RuleListState): URLSearchParams => {
  const { paginationData, selectedPlatform, searchString } = listState;
  const params = new URLSearchParams();

  params.set("page", String(paginationData.page));
  params.set("limit", String(paginationData.limit));

  const search = paginationData.search || searchString;
  if (search) params.set("search", search);
  if (paginationData.sort_by) params.set("sort_by", paginationData.sort_by);
  if (paginationData.sort_order !== "desc") params.set("sort_order", paginationData.sort_order);
  if (selectedPlatform !== RULE_ENGINE_ALL_CHANNELS) params.set("platform", selectedPlatform);

  return params;
};

export const withListStatePage = (listState: RuleListState, page: number): RuleListState => ({
  ...listState,
  paginationData: {
    ...listState.paginationData,
    page,
  },
});

export const buildRuleEngineListPath = (listState: RuleListState): string => {
  const query = ruleListStateToSearchParams(listState).toString();
  return query ? `/rule-engine?${query}` : "/rule-engine";
};

export const resolveInitialListState = (
  searchParams: URLSearchParams,
  navigationListState?: RuleListState
): RuleListState | undefined =>
  parseRuleListStateFromSearchParams(searchParams) ?? navigationListState;
